// src/store/slices/tagSlice.ts

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tagService } from 'src/data/api/helios-service/tagsService';
import { ApiRequestStatus, initApiStatus } from 'src/types/ApiRequestStatus';
import {
  ListTagsRequest,
  Tag,
  CreateTagRequest,
  UpdateTagRequest,
  GetTagRequest,
  DeleteTagRequest,
} from 'src/types/helios/Tags';
import { handleError } from 'src/utils/generalUtils';

export interface TagState {
  tags: Tag[];
  selectedTag: Tag | null;
  fetchTagsApiStatus: ApiRequestStatus;
  getTagApiStatus: ApiRequestStatus;
  createTagApiStatus: ApiRequestStatus;
  updateTagApiStatus: ApiRequestStatus;
  deleteTagApiStatus: ApiRequestStatus;
  nextToken?: string;
}

const initialState: TagState = {
  tags: [],
  selectedTag: null,
  fetchTagsApiStatus: initApiStatus,
  getTagApiStatus: initApiStatus,
  createTagApiStatus: initApiStatus,
  updateTagApiStatus: initApiStatus,
  deleteTagApiStatus: initApiStatus,
  nextToken: undefined,
};

export const fetchTagsApiThunk = createAsyncThunk(
  'tags/fetchTags',
  async (data: ListTagsRequest, { rejectWithValue }) => {
    try {
      const response = await tagService.getAll(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const createTagApiThunk = createAsyncThunk(
  'tags/createTag',
  async (data: CreateTagRequest, { rejectWithValue }) => {
    try {
      const response = await tagService.createTag(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const updateTagApiThunk = createAsyncThunk(
  'tags/updateTag',
  async (data: UpdateTagRequest, { rejectWithValue }) => {
    try {
      const response = await tagService.updateTag(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const getTagApiThunk = createAsyncThunk(
  'tags/getTag',
  async (data: GetTagRequest, { rejectWithValue }) => {
    try {
      const response = await tagService.getTag(data);
      return response;
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

export const deleteTagApiThunk = createAsyncThunk(
  'tags/deleteTag',
  async (data: DeleteTagRequest, { rejectWithValue }) => {
    try {
      await tagService.deleteTag(data);
      return data.tagId; // Return tagId for removing from state
    } catch (error: unknown) {
      return rejectWithValue(handleError(error));
    }
  },
);

const tagsSlice = createSlice({
  name: 'tags',
  initialState,
  reducers: {
    resetAllApiStatus: (state) => {
      state.fetchTagsApiStatus = initApiStatus;
      state.getTagApiStatus = initApiStatus;
      state.createTagApiStatus = initApiStatus;
      state.updateTagApiStatus = initApiStatus;
      state.deleteTagApiStatus = initApiStatus;
    },

    addTagToState: (state, action) => {
      state.tags.push(action.payload);
    },

    updateTagInState: (state, action) => {
      const index = state.tags.findIndex((tag) => tag.tagId === action.payload.tag.tagId);
      if (index !== -1) {
        state.tags[index] = action.payload.tag;
      }
    },

    removeTagFromState: (state, action) => {
      state.tags = state.tags.filter((tag) => tag.tagId !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Tags cases
      .addCase(fetchTagsApiThunk.pending, (state) => {
        state.fetchTagsApiStatus.isLoading = true;
        state.fetchTagsApiStatus.errorMessage = null;
      })
      .addCase(fetchTagsApiThunk.fulfilled, (state, action) => {
        state.fetchTagsApiStatus.isLoading = false;
        state.fetchTagsApiStatus.isSuccess = true;
        state.tags = action.payload.tags;
        state.nextToken = action.payload.nextToken;
      })
      .addCase(fetchTagsApiThunk.rejected, (state, action) => {
        state.fetchTagsApiStatus.isLoading = false;
        state.fetchTagsApiStatus.isSuccess = false;
        state.fetchTagsApiStatus.errorMessage = action.payload as string;
      })

      // Create Tag cases
      .addCase(createTagApiThunk.pending, (state) => {
        state.createTagApiStatus.isLoading = true;
        state.createTagApiStatus.errorMessage = null;
      })
      .addCase(createTagApiThunk.fulfilled, (state, action) => {
        state.createTagApiStatus.isLoading = false;
        state.createTagApiStatus.isSuccess = true;
      })
      .addCase(createTagApiThunk.rejected, (state, action) => {
        state.createTagApiStatus.isLoading = false;
        state.createTagApiStatus.isSuccess = false;
        state.createTagApiStatus.errorMessage = action.payload as string;
      })

      // Update Tag cases
      .addCase(updateTagApiThunk.pending, (state) => {
        state.updateTagApiStatus.isLoading = true;
        state.updateTagApiStatus.errorMessage = null;
        state.updateTagApiStatus.successMessage = null;
      })
      .addCase(updateTagApiThunk.fulfilled, (state, action) => {
        state.updateTagApiStatus.isLoading = false;
        state.updateTagApiStatus.isSuccess = true;
        tagsSlice.caseReducers.updateTagInState(state, {
          type: 'updateTagInState',
          payload: action.payload,
        });
      })
      .addCase(updateTagApiThunk.rejected, (state, action) => {
        state.updateTagApiStatus.isLoading = false;
        state.updateTagApiStatus.isSuccess = false;
        state.updateTagApiStatus.errorMessage = action.payload as string;
      })

      // Get Tag cases
      .addCase(getTagApiThunk.pending, (state) => {
        state.getTagApiStatus.isLoading = true;
        state.getTagApiStatus.errorMessage = null;
      })
      .addCase(getTagApiThunk.fulfilled, (state, action) => {
        state.getTagApiStatus.isLoading = false;
        state.getTagApiStatus.isSuccess = true;
        state.selectedTag = action.payload.tag;
      })
      .addCase(getTagApiThunk.rejected, (state, action) => {
        state.getTagApiStatus.isLoading = false;
        state.getTagApiStatus.isSuccess = false;
        state.getTagApiStatus.errorMessage = action.payload as string;
      })

      // Delete Tag cases
      .addCase(deleteTagApiThunk.pending, (state) => {
        state.deleteTagApiStatus.isLoading = true;
        state.deleteTagApiStatus.errorMessage = null;
      })
      .addCase(deleteTagApiThunk.fulfilled, (state, action) => {
        state.deleteTagApiStatus.isLoading = false;
        state.deleteTagApiStatus.isSuccess = true;
        tagsSlice.caseReducers.removeTagFromState(state, {
          type: 'removeTagFromState',
          payload: action.payload,
        });
      })
      .addCase(deleteTagApiThunk.rejected, (state, action) => {
        state.deleteTagApiStatus.isLoading = false;
        state.deleteTagApiStatus.isSuccess = false;
        state.deleteTagApiStatus.errorMessage = action.payload as string;
      });
  },
});

export const { resetAllApiStatus, updateTagInState, removeTagFromState } =
  tagsSlice.actions;
export default tagsSlice.reducer;
